import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { StatusBadge } from '@/components/common/StatusBadge';
import { getLeadStatusVariant } from '@/components/common/statusVariants';
import type { PotentialDuplicate } from '../api/checkDuplicates';

interface DuplicateWarningDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  duplicates: PotentialDuplicate[];
  onProceed: () => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

/**
 * Warning dialog displayed when potential duplicate leads are found.
 * Shows a table of duplicates and allows the user to proceed anyway or cancel.
 */
export function DuplicateWarningDialog({
  open,
  onOpenChange,
  duplicates,
  onProceed,
  onCancel,
  isSubmitting = false,
}: DuplicateWarningDialogProps) {
  const title =
    duplicates.length === 1
      ? 'Potential Duplicate Found'
      : 'Potential Duplicates Found';

  const handleCancel = () => {
    onCancel();
    onOpenChange(false);
  };

  const handleProceed = () => {
    onProceed();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <div className="flex items-center gap-3">
            {/* Warning Icon */}
            <div className="flex-shrink-0">
              <svg
                className="h-6 w-6 text-amber-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <DialogTitle className="text-amber-600">{title}</DialogTitle>
          </div>
          <DialogDescription className="mt-2">
            We found existing leads that may be duplicates. Please review before
            continuing.
          </DialogDescription>
        </DialogHeader>

        {/* Duplicates Table */}
        <div className="mt-4 max-h-[300px] overflow-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="font-semibold">Job #</TableHead>
                <TableHead className="font-semibold">Customer Name</TableHead>
                <TableHead className="font-semibold">Address</TableHead>
                <TableHead className="font-semibold">Status</TableHead>
                <TableHead className="font-semibold">Match Reasons</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {duplicates.map((duplicate) => (
                <TableRow key={duplicate.id}>
                  <TableCell>
                    <a
                      href={`/leads/${duplicate.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-secondary hover:underline font-medium"
                    >
                      {duplicate.jobNumber || `#${duplicate.id}`}
                    </a>
                  </TableCell>
                  <TableCell>{duplicate.customerName}</TableCell>
                  <TableCell className="max-w-[200px] truncate" title={duplicate.address}>
                    {duplicate.address}
                  </TableCell>
                  <TableCell>
                    <StatusBadge variant={getLeadStatusVariant(duplicate.status)}>
                      {duplicate.status}
                    </StatusBadge>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {duplicate.matchReasons.map((reason, index) => (
                        <Badge
                          key={index}
                          variant="secondary"
                          className="text-xs bg-amber-100 text-amber-800 border-amber-200"
                        >
                          {reason}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <DialogFooter className="mt-6 gap-2 sm:gap-0">
          <Button
            type="button"
            variant="ghost"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={handleProceed}
            disabled={isSubmitting}
            className="border-amber-500 text-amber-600 hover:bg-amber-50"
          >
            {isSubmitting ? 'Saving...' : 'Proceed Anyway'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
